import { useEffect, useId, useLayoutEffect, useRef, type CSSProperties } from "react";
import { Snackbar, type SnackbarProps } from "@mui/material";
import {
  SYSTEM_BANNER_HEIGHT_PX,
  TOAST_STACK_OFFSET_VAR,
  toastAnchorOrigin,
  toastSnackbarSx,
} from "@/shared/ui/systemBannerStyles";
import { useToastStack } from "@/shared/ui/toastStack";

function chainExited(
  slotProps: SnackbarProps["slotProps"],
  onExited: () => void,
): SnackbarProps["slotProps"] {
  const transition = slotProps?.transition;
  if (typeof transition === "function") {
    return {
      ...slotProps,
      transition: (ownerState) => {
        const resolved = transition(ownerState);
        return {
          ...resolved,
          onExited: (node) => {
            onExited();
            resolved.onExited?.(node);
          },
        };
      },
    };
  }
  return {
    ...slotProps,
    transition: {
      ...transition,
      onExited: (node) => {
        onExited();
        transition?.onExited?.(node);
      },
    },
  };
}

type AppSnackbarProps = Omit<SnackbarProps, "anchorOrigin">;

/**
 * App toast. Centered along the bottom below `md`; bottom-left corner from `md`
 * up, one inset above the table pagination bar while one is mounted. Every
 * toast is 280px wide, and wider only so its text stays on one fully visible
 * line. When several are open, newer ones stay on
 * that baseline and older ones move up by each toast's height plus the stack gap.
 */
export default function AppSnackbar({
  open = false,
  sx,
  style,
  slotProps,
  ...rest
}: Readonly<AppSnackbarProps>) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const { offset, upsert, release } = useToastStack(id);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const node = rootRef.current;
    if (!node) return undefined;
    const publish = () => {
      const measured = Math.round(node.getBoundingClientRect().height);
      upsert(measured > 0 ? measured : SYSTEM_BANNER_HEIGHT_PX);
    };
    publish();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, upsert]);

  useEffect(() => release, [release]);

  const transition = chainExited(slotProps, release);

  return (
    <Snackbar
      {...rest}
      ref={rootRef}
      open={open}
      anchorOrigin={toastAnchorOrigin}
      style={{ ...style, [TOAST_STACK_OFFSET_VAR]: `${offset}px` } as CSSProperties}
      slotProps={transition}
      sx={sx == null ? toastSnackbarSx : [toastSnackbarSx, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}
